<template>
  <div class="router-wrapper-without-heading">
    <div class="content-box-no-heading">
      <div class="element-wrapper">
        <simplert :useRadius="true" :useIcon="true" ref="simplert"></simplert>
        <ReportFileUploadModal :report="report" :refetch="fetchReport" :reportSheet="uploadToSheet" />
        <print-report-dialog
          :sheet-ids="getSheetIds()"
          :file-name="report && `${report.report_number}.pdf`"
          :report="report"
        />
        <div v-if="report" :class="{ 'report-owner': isReportOwner() }">
          <div class="element-box">
            <h5 class="form-header">
              <div style="display: flex; align-content: center; align-items: center; justify-content: space-between">
                <div style="display: flex; align-content: center; align-items: center; justify-content: space-between">
                  <div
                    class="circle circle-report"
                    v-if="report.dataValidation"
                    :validation="report.dataValidation"
                  ></div>
                  <div class="circle circle-report" v-if="report.dataProgress" :progress="report.dataProgress"></div>
                  <span class="report-show-title text"> {{ report.report_number }}: {{ report.name }}</span>
                </div>
                <div style="display: flex; align-items: center">
                  <div v-if="isEditable">
                    <loading-button
                      :is-loading="isSubmittingReport"
                      :text="$t('general.submit_report')"
                      class="button-space"
                      :on-click="submitReport"
                      v-if="canEditReport()"
                      color="primary"
                    />

                    <loading-button
                      :is-loading="isCompletingReport"
                      :text="$t('general.complete_report')"
                      class="button-space"
                      :on-click="completeReport"
                      v-if="canCompleteReport()"
                      color="primary"
                    />
                  </div>
                  <div class="icon-button icon-button-spacing" @click="onPrintReport()">
                    <img src="/svg/print_icon.svg" />
                  </div>
                </div>
              </div>
            </h5>

            <div>
              <!-- Using modifiers -->
              <h6 style="cursor: pointer">
                <subheader
                  :on-edit="onEditReportInfo"
                  :on-save="onSaveReportInfo"
                  :is-loading="isSavingReportInfo"
                  :on-cancel="cancelEditingInfo"
                  :is-editing="reportToEdit"
                  :can-edit="canEditReport()"
                  img-src="/svg/buttons/icon_edit.svg"
                >
                  <div class="expand-arrow-wrapper">
                    <div @click="infoExpanded = !infoExpanded">
                      {{ $t("report.report_information") }}
                      <img
                        src="/img/arrow_down.png"
                        style="width: 20px"
                        :class="[infoExpanded ? 'showAnimation' : '']"
                      />
                    </div>
                  </div>
                </subheader>
              </h6>

              <!-- element to collapse -->
              <b-collapse id="collapse2" v-model="infoExpanded" class="collapsable">
                <edit-report-info :report="reportToEdit" :can-edit-report-number="false" v-if="reportToEdit" />
                <report-info :report="report" v-else />
              </b-collapse>
            </div>

            <div>
              <!-- Using modifiers -->
              <h6 class="element-header" style="cursor: pointer">
                <div class="report-subheader">
                  <div class="expand-arrow-wrapper">
                    <a v-b-toggle.collapse3>{{
                      `${$t("report.attachments")} ${
                        report && report.attachments && report.attachments.length > 0
                          ? `(${report.attachments.length})`
                          : ""
                      }`
                    }}</a>
                    <img
                      v-b-toggle.collapse3
                      src="/img/arrow_down.png"
                      style="width: 20px"
                      :class="[attachmentsExpanded ? 'showAnimation' : '']"
                    />
                  </div>
                  <div v-if="canUploadAttachments()">
                    <div class="icon-button">
                      <img src="/svg/buttons/icon_upload.svg" @click="uploadFile" />
                    </div>
                  </div>
                </div>
              </h6>

              <!-- element to collapse -->
              <b-collapse
                id="collapse3"
                v-on:show="attachmentsExpanded = true"
                @hide="attachmentsExpanded = false"
                class="collapsable"
              >
                <div class="image-container">
                  <div v-if="!report || !report.attachments || report.attachments.length <= 0">
                    {{ $t("report.no_attachments") }}
                  </div>
                  <AttachmentCards
                    :attachments="report.attachments"
                    :deletingAttachments="deletingAttachments"
                    :showDeleteConfirmation="showDeleteConfirmation"
                    :openImage="openImage"
                    :canDelete="canDelete && isEditable"
                  />
                </div>
              </b-collapse>
            </div>
            <div class="copy-to-clipboard-container" v-if="USER_ROLE !== 'viewer'">
              <b-btn
                v-b-tooltip.hover
                title="Copy to Clipboard"
                v-clipboard:copy="externalLink"
                v-clipboard:success="copyDone"
                color="#fff"
                class="clipboard-button"
              >
                <i class="far fa-clipboard"></i>
              </b-btn>
              <span
                class="form-control mt-1 mb-3 custom-span-form-control"
                style="width: auto !important; display: inline !important"
                >{{ externalLink }}</span
              >
            </div>
          </div>

          <div class="element-box">
            <h5 class="form-header sheets-header">
              {{ $t("general.sheets") }}
            </h5>
            <div class="row">
              <div class="col-12">
                <div>
                  <sheet-cards :report="report" :select-sheet="selectSheet" :selected-sheet="selectedSheet" />
                  <SheetView
                    v-if="selectedSheet"
                    :reportSheet="selectedSheet"
                    :can-delete="canDelete"
                    :deleting-attachments="deletingAttachments"
                    :show-delete-confirmation="showDeleteConfirmation"
                    :openImage="openImage"
                    :uploadFile="uploadFile"
                    :is-user-report-owner="isUserReportOwner"
                    :report-id="report && report.id"
                    :can-edit-report="canEditReport()"
                    :can-upload-attachments="canUploadAttachments()"
                    :refetch-report="fetchReport"
                    :report-step="report.step"
                    :on-print-report-sheet="onPrintReportSheet"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="notFound" class="element-info-with-icon">
          <div class="element-info-icon">
            <div class="os-icon os-icon-close"></div>
          </div>
          <div class="element-info-text">
            <h5 class="element-inner-header">
              {{ $t("reports_show.no_report_found") }}
            </h5>
            <div class="element-inner-desc">
              {{ $t("reports_show.no_report_found_url") }}
            </div>
          </div>
        </div>
        <div v-else class="element-info-with-icon">
          <div class="element-info-icon">
            <div class="os-icon os-icon-grid-18 element-info-rotate"></div>
          </div>
          <div class="element-info-text">
            <h5 class="element-inner-header">
              {{ $t("report.loading_report") }}
            </h5>
            <div class="element-inner-desc">
              {{ $t("general.please_wait_data_from_cedoon") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import languages from "@/languages";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import * as lodash from "lodash";
import SheetView from "@/components/reports/partials/show/sheet-view.component";
import { getNumberOfColumnsForDataType, getSortGroupForDtaType } from "@/utils/characteristic-data-type";
import ReportFileUploadModal from "@/components/reports/partials/report-file-upload-dialog";
import PrintReportDialog from "@/components/reports/partials/print-report-dialog";
import EditReportInfo from "@/components/reports/partials/show/edit/edit-report-info";
import Subheader from "@/components/reports/partials/show/subheader";
import ReportInfo from "@/components/reports/partials/show/report-info";
import moment from "moment";
import { NewReportStore } from "@/store/NewReportStore";
import AttachmentCards from "@/components/reports/partials/attachment-cards";
import SheetCards from "@/components/reports/partials/sheet-cards.component.vue";
import { ReportStep } from "@/models/graphql-global-types";
import { UserService } from "@/services/UserService";
import LoadingButton from "@/components/core/controls/loading-button.component.vue";
import { parseToNumber } from "@/utils/number";
import { openFile } from "@/utils/open-file";

export default {
  name: "report-show",
  props: {
    isEditable: {
      default: false,
      type: Boolean
    }
  },
  components: {
    ReportInfo,
    EditReportInfo,
    ClipLoader,
    SheetView,
    ReportFileUploadModal,
    PrintReportDialog,
    Subheader,
    AttachmentCards,
    LoadingButton,
    SheetCards
  },
  data() {
    return {
      report: null,
      selectedSheet: null,
      externalLink: null,
      notFound: false,
      availableLanguages: languages.map(l => l.code),
      infoExpanded: false,
      attachmentsExpanded: false,
      deletingAttachments: [],
      canDelete: true,
      woodInfoExpanded: false,
      USER_ROLE: "",
      isPreparingPDF: false,
      showPrintDialog: false,
      isUserReportOwner: false,
      USER_COMPANY: null,
      isSavingReportInfo: false,
      reportToEdit: null,
      uploadToSheet: null,
      isSubmittingReport: false,
      isCompletingReport: false,
      printOnlyCurrentSheet: false
    };
  },
  methods: {
    ...mapActions("reportStore", ["detail", "detailWithSecret"]),
    ...mapActions({
      deleteAttachmentAPI: "reportStore/deleteAttachment"
    }),
    uploadFile(event, sheet) {
      this.uploadToSheet = sheet;
      $("#reportFileUploadModal").modal("show");
    },

    selectSheet(sheet) {
      this.cancelEditingInfo();
      console.log(sheet);
      this.selectedSheet = sheet;
    },
    copyDone() {
      this.$toasted.show(this.$i18n.t("general.copied_to_clipboard"), {
        theme: "primary",
        position: "top-center",
        duration: 1000
      });
    },
    getSheetIds() {
      if (this.printOnlyCurrentSheet && this.selectedSheet) {
        return this.selectedSheet.id;
      }
      const sheetIds = this.$route.query.sheetIds;
      return sheetIds ? (Array.isArray(sheetIds) ? sheetIds : `${sheetIds}`.split(",")) : undefined;
    },
    deleteAttachment(attachment, sheet) {
      this.deletingAttachments.push(attachment.uuid);

      this.deleteAttachmentAPI(attachment.uuid)
        .then(response => {
          this.deletingAttachments = this.deletingAttachments.filter(id => id !== attachment.uuid);
          if (response.data.deleteAttachment === "error") {
            const obj = {
              title: "Error",
              message: "Unknown error occurred",
              type: "error"
            };
            this.$refs.simplert.openSimplert(obj);
          } else {
            if (this.uploadToSheet) {
              this.selectedSheet.attachments = this.selectedSheet.attachments.filter(a => a.uuid !== attachment.uuid);
            } else {
              this.report.attachments = this.report.attachments.filter(a => a.uuid !== attachment.uuid);
            }
            this.fetchReport();
          }
        })
        .catch(e => alert(e));
    },
    showDeleteConfirmation(attachment, sheet) {
      const fn = this.deleteAttachment;
      const confirm = function() {
        fn(attachment, sheet);
      };
      const obj = {
        title: this.$i18n.t("general.confirm"),
        message: this.$i18n.t("reports_show.dialog.message"),
        type: "warning",
        customCloseBtnText: this.$i18n.t("general.cancel"),
        customConfirmBtnText: this.$i18n.t("general.delete"),
        useConfirmBtn: true,
        customConfirmBtnClass: "button-red",
        onConfirm: confirm
      };
      this.$refs.simplert.openSimplert(obj);
    },
    openImage(url) {
      openFile(url, this.$router);
    },
    round(val) {
      return val ? lodash.round(val, 2) : "";
    },
    onEditReportInfo() {
      this.infoExpanded = true;
      this.reportToEdit = Object.assign({}, this.report);
      this.reportToEdit.shipping_date = moment(this.reportToEdit.shipping_date).toDate();
      this.reportToEdit.order_date = this.reportToEdit.order_date && moment(this.reportToEdit.order_date).toDate();
    },
    onSaveReportInfo() {
      return new Promise((resolve, reject) => {
        NewReportStore.saveReportInfo({
          reportId: this.reportToEdit.id,
          properties: {
            name: this.reportToEdit.name,
            orderConfirmation: this.reportToEdit.order_confirmation,
            customerOrderNumber: this.reportToEdit.customer_order_number,
            shippingDate: this.reportToEdit.shipping_date,
            customer: this.reportToEdit.customer,
            supplier: this.reportToEdit.manufacturer,
            productionPlant: this.reportToEdit.production_plant,
            work_order_nr: this.reportToEdit.work_order_nr,
            orderDate: this.reportToEdit.order_date
          }
        })
          .then(result => {
            this.$notify({
              title: this.$t("general.success"),
              text: this.$t("report.success.info.message"),
              type: "success"
            });

            this.isSavingReportInfo = false;
            this.report = this.reportToEdit;
            this.reportToEdit = null;
            resolve(true);
          })
          .catch(_ => {
            reject();
            this.isSavingReportInfo = false;
            this.$notify({
              title: this.$t("general.error.title"),
              text: this.$t("general.error.generic_error"),
              type: "error"
            });
          });
      });
    },
    cancelEditingInfo() {
      this.reportToEdit = null;
    },
    async fetchReport() {
      try {
        const user = (this.user = JSON.parse(sessionStorage.getItem("cedoon_user")));

        this.USER_ROLE = user ? user.role : null;

        this.USER_COMPANY = user ? user.company : null;

        const sheetIds = this.$route.query.sheetIds ? this.$route.query.sheetIds.split(",") : null;

        if (this.USER_ROLE === "viewer" && (!sheetIds || sheetIds.length <= 0)) {
          this.notFound = true;
          return;
        }
        const parseResponse = response => {
          this.report = response.data.report || response.data.reportSecret;
          this.externalLink =
            window.location.origin +
            "/" +
            this.$router.resolve({
              name: "show/report/:secret",
              params: {
                secret: this.report.secret
              }
            }).href;

          if (sheetIds) {
            this.report.sheets = this.report.sheets.filter(sheet => sheetIds.includes(`${sheet.id}`));
          }

          this.report.sheets.forEach(sheet => {
            sheet.characteristics = [];

            sheet.characteristicGroups.forEach(group => {
              sheet.characteristics.push(...group.characteristics);
            });

            sheet.characteristics.sort((a, b) => a.order - b.order);
            sheet.characteristics.forEach(c => {
              c.measurements.sort((a, b) => a.measurements_id - b.measurements_id);
              c.numberOfColumns = getNumberOfColumnsForDataType(c.dataType);
              c.sortGroup = getSortGroupForDtaType(c.dataType);
            });

            if (sheet.attachments) {
              sheet.attachments = Object.values(sheet.attachments);
            }

            sheet.columns = 10;

            const properties = {};
            sheet.product.properties.stringValue.forEach(prop => {
              properties[prop.propertyType] = prop;
            });
            sheet.product.properties.singleValue.forEach(prop => {
              properties[prop.propertyType] = prop;
            });
            sheet.product.properties.multpileValues.forEach(prop => {
              properties[prop.propertyType] = prop.values;
            });

            sheet.product.properties = properties;

            sheet.hasWoodInfo =
              sheet.info.woodNr ||
              sheet.info.woodMoisture ||
              sheet.info.roomTemperature ||
              sheet.info.insertionDirection ||
              sheet.info.woodDensity ||
              sheet.info.roomMoisture;
          });

          if (this.report.attachments) {
            this.report.attachments = Object.values(this.report.attachments);
          }

          this.isUserReportOwner = this.report.ownerCompany === this.USER_COMPANY;

          this.report.sheets.forEach(s => {
            s.screwDiameter = parseToNumber(s.product.screwDiameter?.replace(/[^0-9,.]/g, ""));
            s.screwLength = parseToNumber(s.product.screwLength?.replace(/[^0-9,.]/g, ""));
          });

          this.report.sheets = lodash.sortBy(
            this.report.sheets,
            "product.customer",
            "product.productGroup.name",
            "product.screwType",
            "product.head",
            "product.recessType",
            "screwDiameter",
            "screwLength",

            "product.threadType",
            "product.surfaceColor"
          );

          if (this.selectedSheet) {
            this.selectedSheet = this.report.sheets.find(s1 => s1.id === this.selectedSheet.id);
          }
        };

        if (this.$route.params.id) {
          const id = this.$route.params.id;

          const response = await this.detail(id);
          parseResponse(response);
        } else if (this.$route.params.secret) {
          this.canDelete = false;
          const response = await this.detailWithSecret(this.$route.params.secret);
          parseResponse(response);

          if (this.report.attachments) {
            this.report.attachments = Object.values(this.report.attachments);
          }

          this.report.attachments.forEach(a => (a.isDeleted = false));
        } else {
          this.notFound = true;
        }
      } catch (e) {
        console.error(e);
        this.notFound = true;
      }
    },
    canEditReport() {
      return (
        UserService.user &&
        (this.report.step === ReportStep.IN_PROGRESS || this.report.step === ReportStep.CREATED) &&
        this.$props.isEditable
      );
    },
    canUploadAttachments() {
      return (
        UserService.user &&
        (this.report.step === ReportStep.IN_PROGRESS ||
          this.report.step === ReportStep.CREATED ||
          this.report.step === ReportStep.DONE_GATHERING) &&
        this.$props.isEditable &&
        (UserService.isUserSupervisor() || UserService.isUserAdmin() || UserService.isUserTester())
      );
    },
    canCompleteReport() {
      const user = UserService.user;
      return (
        (UserService.isUserAdmin() || UserService.isUserSupervisor()) && this.report.step === ReportStep.DONE_GATHERING
      );
    },
    async showInputDialog(title, text, confirm, invalidMessage) {
      return this.$swal({
        title: title,
        input: "text",
        html: text,
        showCancelButton: true,
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonText: confirm,
        focusConfirm: false,
        confirmButtonColor: "#232f5d",
        cancelButtonColor: "#c5c8cf",
        inputValidator: value => {
          if (!value) {
            return invalidMessage;
          }
        }
      });
    },
    async submitReport() {
      const result = await this.showInputDialog(
        this.$t("general.submit_report"),
        this.$t("submit_report.text"),
        this.$t("general.submit_report"),
        this.$t("submit_report.error")
      );

      if (result && result.value) {
        const name = result.value;
        this.isSubmittingReport = true;
        NewReportStore.completeTestingOfReport({
          reportId: this.report.id,
          recordedBy: name
        })
          .then(r => {
            this.isSubmittingReport = false;
            this.$notify({
              title: this.$t("general.success"),
              text: this.$t("submit_report.success"),
              type: "success"
            });
            if (UserService.isUserTester()) {
              this.$router.replace("/reports");
            } else {
              this.fetchReport();
            }
          })
          .catch(_ => {
            this.isSubmittingReport = false;
            this.$notify({
              title: this.$t("general.error.title"),
              text: this.$t("general.error.generic_error"),
              type: "error"
            });
          });
      }
    },
    async completeReport() {
      const result = await this.showInputDialog(
        this.$t("general.complete_report"),
        this.$t("complete_report.text"),
        this.$t("general.complete_report"),
        this.$t("complete_report.error")
      );

      if (result && result.value) {
        const name = result.value;
        this.isCompletingReport = true;
        NewReportStore.completeReport({
          reportId: this.report.id,
          supervisor: name
        })
          .then(r => {
            this.isCompletingReport = false;
            this.$notify({
              title: this.$t("general.success"),
              text: this.$t("complete_report.success"),
              type: "success"
            });
            this.$router.replace("/reports");
          })
          .catch(_ => {
            this.isCompletingReport = false;
            this.$notify({
              title: this.$t("general.error.title"),
              text: this.$t("general.error.generic_error"),
              type: "error"
            });
          });
      }
    },
    getDataValidation(reportOrSheet) {
      const validation = reportOrSheet.dataValidation;
      return validation ? `${validation}`.toLowerCase() : null;
    },
    isReportOwner() {
      return UserService.user && UserService.user.company === (this.report && this.report.ownerCompany);
    },
    onPrintReportSheet() {
      console.log("print sheet");
      this.printOnlyCurrentSheet = true;
      $("#printReportDialog").modal("show");
    },
    onPrintReport() {
      console.log("print report");
      this.printOnlyCurrentSheet = false;
      $("#printReportDialog").modal("show");
    }
  },

  mounted() {
    this.fetchReport();
  }
};
</script>

<style scoped lang="scss">
@import "./src/assets/theme/_variables.scss";
@import "./src/assets/controls/mix-ins/_hover.scss";
@import "./src/assets/shadows/shadows";

.button-space {
  margin-left: 16px;
}
.sheets-header {
  display: flex;
  align-items: center;
}

.report-subheader {
  display: flex;
  justify-content: space-between;
}

.report-owner .sheet-card.tested_nok_critical {
  box-shadow: 0px 5px 12px rgba(126, 142, 177, 0.2), 0px 0px 0px 2px $error-color;
}
.report-owner .sheet-card.tested_nok {
  box-shadow: 0px 5px 12px rgba(126, 142, 177, 0.2), 0px 0px 0px 2px $warning-color;
}
.circle-report {
  margin-right: $spacing-unit * 2;
}
.copy-to-clipboard-container {
  display: flex;
  align-items: center;
}
.copy-to-clipboard-container .custom-span-form-control {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
}
.icon-button-spacing {
  margin-left: $spacing-unit * 2;
}
.clipboard-button {
  @include shadow;
  padding: $spacing-unit;
  border: 0px;
  height: fit-content;
  margin-right: $spacing-unit * 2;
  i {
    height: 22px;
    width: 22px;
    font-size: 22px;
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: center;
  }
}
</style>
